<template>
  <div
    :class="[
      'generic-page',
      colorModeClass,
      backgroundColorClass,
      {
        '--with-fitter-badge': showFitterBadge,
        '--with-comments': is_commentable,
        '--with-toolbar-spacer': shouldShowToolbarSpacer
      }
    ]"
    :style="styles"
  >
    <toolbar
      v-if="showToolbar"
      :transparent="isToolbarTransparent"
      :use-account-styles="!isToolbarTransparent"
      :back="showBackButton"
      :page-id="page_id"
      :logo="useGlobalToolbar && (isDesktop ? !show_sidebar_navigation : true)"
      fixed
      :with-menu="
        isDesktop
          ? !show_sidebar_navigation && show_menu_button
          : show_menu_button
      "
      :with-navigation="
        show_desktop_navigation_links && !show_sidebar_navigation
      "
    >
      <template v-slot:right>
        <template v-if="!isElementHidden(HIDDEN_ELEMENTS.TOOLBAR_ACTIONS)">
          <scheduling-button
            item-type="page"
            :item-id="page_id"
            :page-content-type="page_content_type"
            :title="page_title"
            :can-add-to-schedule="show_add_to_schedule_button"
          />

          <bookmark-button
            v-if="isHomepage ? false : show_bookmark_button"
            :page-id="page_id"
          />

          <div id="page-toolbar-actions" />
        </template>
      </template>
    </toolbar>

    <template v-if="showPageContent">
      <blocks-container
        :blocks="blocks"
        :page-id="page_id"
        @init="handleContainerInit"
      />

      <cta-row v-if="showCtaRow" :key="page_id" white-bg overlay>
        <div class="w-100">
          <template v-if="is_commentable || isPresent(showThread)">
            <message-thread
              :key="threadId"
              class="generic-page__message-thread"
              :placeholder="
                $t(
                  'components.pageTemplates.genericTemplate.addCommentPlaceHolder'
                )
              "
              collapsable
              with-participants
              is-comment-thread
              :message-thread-id="threadId"
            />
          </template>
        </div>
        <template v-if="displayablePageActions.length > 0">
          <template v-for="action in displayablePageActions">
            <template
              v-if="action.action_type === PAGE_ACTION_TYPES.MARK_COMPLETE"
            >
              <mark-complete
                :key="action.id"
                :block-action="action.block_action"
                :text="$translate(action, 'text')"
                :page-title="page_title"
                :page-id="page_id"
                :root-id="root_id"
                @hidden="handleActionHidden"
              />
            </template>

            <template
              v-else-if="
                action.action_type === PAGE_ACTION_TYPES.GENERIC_BUTTON
              "
            >
              <generic-button
                :key="action.id"
                :block-action="genericButtonBlockAction(action)"
                @hidden="handleActionHidden"
              />
            </template>

            <template
              v-else-if="action.action_type === PAGE_ACTION_TYPES.START"
            >
              <start
                :key="action.id"
                :page-title="page_title"
                :page-id="page_id"
                :text="$translate(action, 'text')"
                :schedule="schedule"
                :access="page_access"
                :page-type="page_content_type"
                :action="action"
                :block-action="action.block_action"
                :post-action-button-text="
                  $translate(action, 'post_action_button_text')
                "
                @hidden="handleActionHidden"
              />
            </template>
          </template>
        </template>
      </cta-row>
    </template>

    <template
      v-else-if="!hasFetchedAccess || (!hasCheckedAuthentication && isLocked)"
    >
      <page-loader />
    </template>

    <template v-else>
      <div class="container">
        <div
          class="text-center h-full px-3 d-flex flex-column justify-content-center"
        >
          <v-icon icon="lock" class="text-grey-50" size="3rem" />
          <h2 class="mb-0">
            {{ $t("components.pageTemplates.genericTemplate.notAccessTitle") }}
          </h2>

          <template v-if="isLockedByPrivate">
            <p class="lead mt-2 mb-5">
              {{
                $t("components.pageTemplates.genericTemplate.loginToHaveAccess")
              }}
            </p>
          </template>
          <template v-else-if="isLockedFromPurchase">
            <p class="lead mt-2 mb-5">
              {{
                $t("components.pageTemplates.genericTemplate.purchaseToUnlock")
              }}
            </p>
          </template>
          <template v-else-if="isLockedByDrip">
            <p class="lead mt-2 mb-5">
              <template v-if="availableAfter">
                {{
                  $t("components.pageTemplates.genericTemplate.availableIn", {
                    in: timeAgo(availableAfter, true)
                  })
                }}
              </template>
              <template v-else>
                {{
                  $t("components.pageTemplates.genericTemplate.notAvailable")
                }}
              </template>
            </p>
          </template>
          <template v-else>
            <p class="lead mt-2 mb-5">
              {{
                $t(
                  "components.pageTemplates.genericTemplate.loginToReviewContent"
                )
              }}
            </p>
          </template>
          <v-button
            v-if="isLockedFromPurchase"
            block
            @click="triggerPurchase"
            class="mb-2"
          >
            {{ $t("components.pageTemplates.genericTemplate.purchase") }}
          </v-button>

          <v-button
            v-if="!isAuthenticated"
            secondary
            @click="() => $router.push('#auth')"
          >
            {{ $t("components.pageTemplates.genericTemplate.login") }}
          </v-button>
        </div>
      </div>
    </template>

    <page-action-panel-modal :page-id="page_id" />

    <v-made-with-movement :auto-detect-color="!!background_color" />
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters, mapActions } from "vuex"

import { resolveCurrentSchedule } from "@shared/lib/schedule-utils"
import { STAGES as CHECKOUT_STAGES } from "@/lib/checkout-utils"
import isPreviewContext from "@/lib/is-platform/is-preview-context"
import { isPresent, isEmpty, isBuilderContext } from "@/lib/utils"
import enumValidator from "@/lib/validators/enum-validator"
import EventBus, { EVENTS } from "@/lib/event-bus"
import { pageMeta } from "@/lib/pwa-meta"
import { QUERY_PARAMS } from "@/lib/messages-utils"
import { HIDDEN_ELEMENTS } from "@/lib/constants"
import { COLOR_MODES, ALL_COLORS, BACKGROUND_COLORS } from "@/lib/stream-utils"
import { timeAgo } from "@/lib/date-utils"
import { hasNonZeroStyleValue } from "@shared/styles-utils"
import Toolbar from "@/components/Toolbar"
import PlaceholderRect from "@/components/PlaceholderRect"

import { getPageAccess, findAccessRoot, REASONS } from "@/lib/access-utils"

const PAGE_ACTION_TYPES = {
  MARK_COMPLETE: "mark_complete",
  GENERIC_BUTTON: "generic_button",
  START: "start"
}

const PageLoader = {
  render(h) {
    // TODO: import h from vue in vue 3
    return h("div", { class: "container pt-1" }, [
      h(PlaceholderRect, { props: { width: "100%", height: "250px" } }),
      h(PlaceholderRect, { props: { width: "80%", height: "32px" } }),
      h(PlaceholderRect, { props: { width: "35%", height: "32px" } })
    ])
  }
}

export const withLoader = component => ({
  component,
  loading: PageLoader
})

export default {
  components: {
    BlocksContainer: () =>
      withLoader(import("@/components/stream/BlocksContainer")),
    Toolbar,
    PageLoader,
    CtaRow: () => import("@/components/CtaRow"),
    // PageSiblings: () => import("@/components/toolbar/PageSiblings"),
    PageActionPanelModal: () =>
      import("@/components/modals/PageActionPanelModal"),
    BookmarkButton: () => import("@/components/toolbar/BookmarkButton"),
    SchedulingButton: () => import("@/components/toolbar/SchedulingButton"),
    Start: () => import("@/components/page-actions/Start"),
    MarkComplete: () => import("@/components/page-actions/MarkComplete"),
    GenericButton: () => import("@/components/page-actions/GenericButton"),
    MessageThread: () => import("@/components/message-thread/Thread")
  },

  props: {
    page_id: { type: [Number, String], required: true },
    root_id: { type: [Number, String], default: null },
    page_title: { type: String, default: "" },
    page_content_type: { type: String, default: null },
    path: { type: String, default: null },
    data: { type: Object, default: () => ({}) },
    data_fallbacks: { type: Object, default: () => ({}) },
    attributes: { type: Object, default: () => ({}) },
    page_access: { type: Object, default: () => ({}) },
    page_schedule: { type: Object, default: () => ({}) }, // Legacy cache support (remove when schedules are receive by making a request)
    page_data: { type: Object, default: () => ({}) }, // Legacy cache support (remove when pages are deployed with top level 'path' prop)
    page_schedules: { type: Array, default: null },
    page_actions: { type: Array, default: () => [] },
    siblings: { type: Array, default: () => [] },
    blocks: { type: Array, required: true },
    is_root: { type: Boolean, default: false },
    show_navigation: { type: Boolean, default: true },
    show_sidebar_navigation: { type: Boolean, default: true },
    show_desktop_navigation_links: { type: Boolean, default: false },
    show_menu_button: { type: Boolean, default: true },
    show_back_button: { type: Boolean, default: true },
    show_bookmark_button: { type: Boolean, default: true },
    show_add_to_schedule_button: { type: Boolean, default: false },
    show_bottom_padding: { type: Boolean, default: true },
    show_tabbar: { type: Boolean, default: true },
    color_mode: { type: String, default: "" },
    background_color: {
      type: String,
      default: null,
      validator: enumValidator([null, ...Object.values(ALL_COLORS)])
    },
    use_global_toolbar: { type: Boolean, default: false },
    og_title: { type: String, default: "" },
    og_description: { type: String, default: "" },
    og_image: { type: Object, default: null },
    is_commentable: { type: Boolean, default: false },
    message_thread_id: { type: [String, Number], default: null }, // TODO: Remove
    content_root_id: { type: [Number, String], default: null },
    container_width: { type: Number, default: null }
  },

  data() {
    return {
      showThread: null,
      hasFetchedAccess: false,
      showBackButton: this.show_back_button,
      isCtaRowActionHidden: false,
      BACKGROUND_COLORS,
      PAGE_ACTION_TYPES,
      HIDDEN_ELEMENTS
    }
  },

  provide() {
    return {
      page_id: this.page_id
    }
  },

  head() {
    return pageMeta({
      title: isPresent(this.og_title) ? this.og_title : this.page_title,
      ogTitle: isPresent(this.og_title) ? this.og_title : this.page_title,
      url: this.pagePath ? this.global?.app.url + this.pagePath : null,
      description: isPresent(this.og_description) ? this.og_description : null,
      image: isPresent(this.og_image) ? this.og_image.image : null
    })
  },

  computed: {
    ...mapState(["global"]),
    ...mapState("responsive", ["isDesktop"]),
    ...mapState("pages", ["pageSubscriptions", "pageProgress"]),
    ...mapGetters("pages", ["isToolbarHidden", "submissionsCountByPage"]),
    ...mapGetters("auth", [
      "hasCheckedAuthentication",
      "isAuthenticated",
      "currentUser"
    ]),
    ...mapGetters("access", ["isSubscriberOnly"]),
    ...mapGetters(["showFitterBadge", "appAccess"]),
    ...mapGetters("localisation", ["currentLocale"]),
    ...mapGetters("router", ["stack"]),
    ...mapGetters("ui", ["isElementHidden"]),

    isReady() {
      return this.hasCheckedAuthentication && this.hasFetchedAccess
    },

    showPageContent() {
      if (isPreviewContext() || isBuilderContext()) {
        return true
      }

      return !!this.pageAccess.granted
    },

    pageAccess() {
      if (isBuilderContext()) return { granted: true }

      return getPageAccess({
        pageId: this.page_id,
        currentPageId: this.page_id,
        currentUser: this.currentUser,
        appAccess: this.appAccess,
        pageSubscriptions: this.pageSubscriptions,
        pageProgresses: this.pageProgress
      })
    },

    isLocked() {
      return !this.pageAccess.granted
    },

    isLockedFromAuthentication() {
      return (
        this.isLocked &&
        this.pageAccess.reason === REASONS.REQUIRE_AUTHENTICATION
      )
    },

    isLockedByDrip() {
      return this.isLocked && this.pageAccess.reason === REASONS.IS_SCHEDULED
    },

    isLockedFromPurchase() {
      return (
        this.isLocked && this.pageAccess.reason === REASONS.REQUIRE_PURCHASE
      )
    },

    isLockedByPrivate() {
      return this.isLocked && this.pageAccess.reason === REASONS.IS_PRIVATE
    },

    isGlobalInheritance() {
      return !findAccessRoot({
        pageId: this.page_id,
        currentPageId: this.page_id
      })[0]
    },

    availableAfter() {
      return this.isLockedByDrip
        ? this.pageAccess.data?.available_after || null
        : null
    },

    showCtaRow() {
      return (
        this.is_commentable ||
        isPresent(this.showThread) ||
        (this.displayablePageActions.length > 0 && !this.isCtaRowActionHidden)
      )
    },

    displayablePageActions() {
      return this.page_actions.filter(a => a.action_type !== "track_progress")
    },

    isPageSubscribed() {
      return !!this.pageSubscriptions?.[this.page_id]
    },

    useGlobalToolbar() {
      return this.is_root ? !!this.use_global_toolbar : false
    },

    isToolbarTransparent() {
      return (
        (this.isDesktop && this.show_sidebar_navigation) ||
        !this.useGlobalToolbar
      )
    },

    pagePath() {
      if (isPresent(this.path)) return this.path

      return this.page_data?.["page.path"]
    },

    isHomepage() {
      return this.is_root && this.pagePath === "/"
    },

    submissionsCount() {
      return this.submissionsCountByPage(this.page_id)
    },

    schedule() {
      const schedules =
        this.page_schedules || [this.page_schedule].filter(s => s?.id) || []

      return resolveCurrentSchedule(schedules)
    },

    showToolbar() {
      if (
        (this.isElementHidden(HIDDEN_ELEMENTS.TOOLBAR) &&
          this.stack[0] === this.$route.path) ||
        this.isToolbarHidden(this.page_id)
      )
        return false

      return this.show_navigation
    },

    shouldShowToolbarSpacer() {
      return !this.isToolbarTransparent && !this.hasNonZeroToolbarMargin()
    },

    colorModeClass() {
      return isEmpty(this.color_mode)
        ? ""
        : this.color_mode === COLOR_MODES.DARK
        ? "--dark-mode"
        : "--light-mode"
    },

    backgroundColorClass() {
      return this.background_color
        ? `--bg-${this.background_color}`
        : "--bg-no-fill"
    },

    isDarkBackground() {
      return this.background_color === ALL_COLORS.BLACK
    },

    backgroundColor() {
      return BACKGROUND_COLORS[this.background_color]
    },

    threadId() {
      return isPresent(this.message_thread_id)
        ? this.message_thread_id
        : this.showThread
    },

    pageDataPayload() {
      if (
        Object.keys(this.data || {}).length === 0 &&
        Object.keys(this.attributes || {}).length > 0
      ) {
        return this.attributes
      }

      return this.data || {}
    },

    styles() {
      return this.container_width
        ? {
            "--container-width": `${this.container_width}px`
          }
        : {}
    }
  },

  created() {
    // Call setPage() here (before children mount) so that currentPage.title is
    // already set when TextBlock children call setupContent() in their mounted()
    // hooks. Without this, TextBlock resolves the page.title built-in field
    // against the *previous* page's title (stale Vuex state), because
    // GenericTemplate.mounted() fires after all children have already mounted.
    this.setPage()
  },

  activated() {
    this.setPage()

    this.$nextTick(() => {
      EventBus.$emit(EVENTS.PAGE_BLOCKS_RENDERED)
    })
  },

  mounted() {
    this.setPage()

    this.loadAccess({ pageId: this.page_id }).then(() => {
      this.hasFetchedAccess = true
    })

    // Evaluate on mount to prevent thread from disappearing when the query param is removed
    this.showThread = this.$route?.query[QUERY_PARAMS.SHOW_THREAD]

    EventBus.$on(
      EVENTS.EXISTING_PRODUCT_SUBSCRIPTION,
      this.handleExistingProductSubscription
    )
  },

  beforeDestroy() {
    EventBus.$off(
      EVENTS.EXISTING_PRODUCT_SUBSCRIPTION,
      this.handleExistingProductSubscription
    )
  },

  methods: {
    isPresent,
    timeAgo,

    ...mapMutations("pages", ["setPageData", "setCurrentPage"]),
    ...mapActions("pages", ["loadAccess"]),
    ...mapActions("localisation", ["fetchTranslations"]),

    handleContainerInit() {
      this.$nextTick(() => {
        this.$emit("blocksRendered")
        EventBus.$emit(EVENTS.PAGE_BLOCKS_RENDERED)
      })
    },

    hasNonZeroToolbarMargin() {
      return hasNonZeroStyleValue(
        this.global.toolbar?.styles || {},
        "margin",
        this.isDesktop
      )
    },

    handleActionHidden() {
      this.isCtaRowActionHidden = true
    },

    triggerPurchase() {
      EventBus.$emit(EVENTS.TRIGGER_CHECKOUT, {
        productIds: this.pageAccess.data.product_ids,
        overlay: this.pageAccess.data.purchase_overlay
      })
    },

    setPage() {
      this.setCurrentPage({
        id: this.page_id,
        rootId: this.root_id,
        title: this.page_title,
        contentRootId: this.content_root_id,
        path: this.pagePath,
        actions: this.page_actions,
        access: this.page_access,
        type: this.page_content_type,
        data_fallbacks: this.data_fallbacks || {},
        currentSchedule: this.schedule || null,
        show_navigation: this.show_navigation,
        show_sidebar_navigation: this.show_sidebar_navigation,
        show_tabbar: this.show_tabbar
      })

      // Setting this here prevents the toolbar from updating before the page has been set
      this.showBackButton = this.isHomepage ? false : !!this.show_back_button

      this.setPageData({
        pageId: this.page_id,
        data: this.pageDataPayload
      })
    },

    showAuthModal() {
      if (!this.$route.hash?.startsWith("#auth")) {
        this.$router.push("#auth")
      }
    },

    genericButtonBlockAction(action) {
      const blockAction = action?.block_action
      if (!blockAction) return {}

      const translatedText = this.$translate(action, "text")

      return {
        ...blockAction,
        text: blockAction.text || translatedText || ""
      }
    }
  },

  watch: {
    isReady: {
      immediate: true,
      handler: function() {
        if (this._inactive) return
        if (!this.isReady) return

        if (
          this.$route.hash &&
          this.$route.hash.startsWith(CHECKOUT_STAGES.START)
        )
          return

        if (this.isLockedFromPurchase) {
          // This flow shows the auth modal before the purchase modal
          // if the user is logged out and we are using global inheritance
          const showAuthFirst =
            this.isGlobalInheritance &&
            this.appAccess.prompt_purchase_post_sign_up &&
            this.isSubscriberOnly

          if (EventBus.hasListener(EVENTS.POSTING_SIGN_UP_ACTION)) {
            // Bind event from AuthModal
            EventBus.$once(EVENTS.FINISH_SIGN_UP_ACTION, this.triggerPurchase)
          } else if (showAuthFirst && !this.isAuthenticated) {
            this.showAuthModal()
          } else if (!(this.$router.hash || "").startsWith("#auth")) {
            this.triggerPurchase()
          }
        } else if (this.isLockedFromAuthentication) {
          this.showAuthModal()
        }
      }
    },

    showPageContent() {
      if (this.showPageContent) {
        this.$nextTick(() => {
          window.scroll(0, 0)
        })
      }
    },

    // Leaving a page clears its subscription, so restore the CTA row a post-join hide collapsed.
    isPageSubscribed(subscribed) {
      if (!subscribed) this.isCtaRowActionHidden = false
    },

    currentLocale: {
      immediate: true,
      handler: function() {
        if (!this.currentLocale || !this.pagePath) return

        this.fetchTranslations({ path: this.pagePath })
      }
    },

    show_tabbar() {
      this.setPage()
    },

    show_navigation() {
      this.setPage()
    },

    show_sidebar_navigation() {
      this.setPage()
    },

    show_back_button() {
      this.setPage()
    },

    page_title() {
      this.setPage()
    },

    schedule() {
      this.setPage()
    },

    pageDataPayload: {
      immediate: true,
      deep: true,
      handler() {
        this.setPageData({
          pageId: this.page_id,
          data: this.pageDataPayload
        })
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";

$toolbar-spacer-offset-block-types: hero_block, iframe_block, livestream_block,
  map_block, layout_block, composed_block, code_block;

// Spacer is hidden only when the first block is a full-bleed offset type, not when
// a composed_block wrapper is followed by text/other blocks (matches safe-space drills).
$toolbar-spacer-hide-leaf-types: hero_block, iframe_block, livestream_block,
  map_block, layout_block, code_block;

.generic-page {
  background: $color-ui--background;
  color: $color-ui--grey-90;

  // // Safe Space Top for the fixed toolbar with margin
  &:not(.--with-toolbar-spacer) {
    @each $type in $toolbar-spacer-hide-leaf-types {
      @for $depth from 0 through 5 {
        $spacer-hide--chain: "";

        @if $depth > 0 {
          @for $i from 1 through $depth {
            $spacer-hide--chain: $spacer-hide--chain +
              '> .block:first-child[data-type="composed_block"] > [data-role="block-container"] ';
          }
        }

        $spacer-hide--chain: $spacer-hide--chain +
          '> .block:first-child[data-type="#{$type}"]';

        > [data-role="fixed-toolbar"]:has(+ .blocks-container
            #{$spacer-hide--chain})
          .v-toolbar__spacer {
          display: none !important;
        }
      }
    }

    > .v-toolbar__wrapper:first-child {
      .v-toolbar {
        margin-top: env(safe-area-inset-top);
      }
    }

    .v-toolbar__container {
      padding-top: 0 !important;
    }
  }

  &.--bg-white {
    @include bg-white;

    & > .blocks-container {
      @include bg-white;
    }
  }

  &.--bg-black {
    @include bg-black;

    & > .blocks-container {
      @include bg-black;
    }
  }

  &.--bg-light_grey {
    @include bg-light-grey;

    & > .blocks-container {
      @include bg-light-grey;
    }
  }

  &.--bg-grey {
    @include bg-dark-grey;

    & > .blocks-container {
      @include bg-dark-grey;
    }
  }

  &.--bg-primary {
    @include bg-primary;

    & > .blocks-container {
      @include bg-primary;
    }
  }

  &.--bg-secondary {
    @include bg-secondary;

    & > .blocks-container {
      @include bg-secondary;
    }
  }

  &.--bg-secondary_2 {
    @include bg-secondary-2;

    & > .blocks-container {
      @include bg-secondary-2;
    }
  }

  &.--bg-secondary_3 {
    @include bg-secondary-3;

    & > .blocks-container {
      @include bg-secondary-3;
    }
  }

  &.--bg-dark {
    @include bg-dark;

    & > .blocks-container {
      @include bg-dark;
    }
  }

  &.--bg-light {
    @include bg-light;

    & > .blocks-container {
      @include bg-light;
    }
  }

  // Safe Space Top - No Toolbar
  & > .blocks-container {
    & > .block:first-child {
      padding-top: env(safe-area-inset-top);

      &[data-type="hero_block"],
      &[data-type="livestream_block"] {
        padding-top: inherit;
      }
    }
  }

  // Safe Space Top - Inline Toolbar
  & > [data-role="inline-toolbar"] + .blocks-container,
  &
    > [data-role="fixed-toolbar"]:not([data-transparent-toolbar])
    + .blocks-container {
    & > .block:first-child {
      padding-top: inherit;
    }
  }

  // Safe Space Top - Fixed Toolbar
  // Max 5 levels of nesting supported
  & > [data-transparent-toolbar] + .blocks-container,
  &:not(.--with-toolbar-spacer) > .blocks-container {
    & > .block:first-child,
    &
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child, // .block:first-child > composed-block > .block
    &
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child, // .block:first-child > composed-block > composed-block > .block
    &
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child, // .block:first-child > composed-block > composed-block > composed-block > .block
    &
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child, // .block:first-child > composed-block > composed-block > composed-block > composed-block > .block
    &
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child[data-type="composed_block"]
      > [data-role="block-container"]
      > .block:first-child // .block:first-child > composed-block > composed-block > composed-block > composed-block > composed-block > .block
 {
      padding-top: $toolbar--height;
      padding-top: calc(#{$toolbar--height} + env(safe-area-inset-top));

      @each $type in $toolbar-spacer-offset-block-types {
        &[data-type="#{$type}"] {
          padding-top: inherit;
        }
      }

      &[data-type="image_block"] {
        padding-top: env(safe-area-inset-top);
      }

      &[data-type="hero_block"] {
        .hero {
          padding-top: env(safe-area-inset-top);
        }
      }
    }
  }
}

.generic-page__message-thread {
  &.message-thread {
    min-height: initial;
    max-height: 68vh;

    .overlay-pill {
      display: flex;
      flex-direction: row-reverse;
      color: $color-ui--grey-60;
      background: transparent;
      backdrop-filter: none;

      .v-icon {
        margin-left: 0.5rem;
      }
    }

    .thread__footer {
      border: none;
      margin: 0;
      width: 100%;
      box-shadow: none;
      border: 2px solid $color-ui--grey-30;
      max-width: initial;
    }

    .message-thread__content {
      padding-top: 0;
      padding-bottom: 0;
    }

    &.--comments {
      flex-direction: column;

      .message-thread__footer-wrapper {
        margin: 0 0 0.75rem;
        padding-top: 0;
      }
    }

    &.--thread {
      & > .message-thread__content-wrapper {
        padding-left: 0;

        .thread__line-vert {
          left: 16px;
          top: 42px;
          height: 100%;
        }

        .thread__corner {
          display: none;
        }

        .message-thread__content {
          padding-left: 1px;
        }
      }

      .message-thread__actions {
        margin-bottom: 0.5rem;

        .v-button {
          margin-left: 2px;
          margin-bottom: -4px;
        }
      }

      .thread__footer {
        .thread__line-vert {
          height: 12px;
          top: -10px;
          background: $color-ui--grey-20;
        }
      }
    }
  }
}

.action-pages_start-time {
  color: $color-ui--grey-80;
  font-weight: $font-weight--semi-bold;
  padding: $size--3 0 $size--3;
  margin-bottom: $size--2;
}
</style>
